import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { ArrowRight, Home } from "lucide-react";
import { useGameMode } from "@/contexts/GameModeContext";
import * as CookieManager from "@/lib/managers/CookieManager";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { CompletedWord } from "./CompletedWord";
import { LetterBox } from "./LetterBox";
import { useNavigate } from "react-router-dom";
import { formatDate, getEffectiveDate } from "@/lib/dateUtils";

// This component is no longer actively used as we're redirecting to welcome page instead
// Keeping it for future reference or if redirection behavior changes

interface InProgressGameOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const InProgressGameOverlay = ({ isOpen, onClose }: InProgressGameOverlayProps) => {
  const [visible, setVisible] = useState(false);
  const { currentMode } = useGameMode();
  const [currentWord, setCurrentWord] = useState("");
  const [currentLevel, setCurrentLevel] = useState(1);
  const [attempts, setAttempts] = useState(0);
  const [guesses, setGuesses] = useState<{ letter: string; status: string }[][]>([]);
  const [hasGuesses, setHasGuesses] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Delay showing the overlay slightly to ensure proper animation
    if (isOpen) {
      // CRITICAL FIX: Set the "shown" flag BEFORE rendering overlay
      // This prevents race conditions on quick reloads
      const mode = currentMode;
      const prefix = mode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
      
      // Set flag in sessionStorage (cleared on tab close) to prevent re-showing
      try {
        sessionStorage.setItem(`${prefix}in_progress_overlay_shown_this_session`, 'true');
        console.log('[InProgressOverlay] Set session flag before showing overlay');
      } catch (error) {
        console.error('[InProgressOverlay] Error setting session flag:', error);
      }

      setTimeout(() => {
        setVisible(true);
      }, 100);
      
      // For daily mode, validate that saved game is from today
      const savedDate = directCookieAccess.getCookie(`${prefix}game_persistence_date`);
      const today = formatDate(getEffectiveDate());
      
      if (mode === 'daily' && savedDate !== today) {
        console.log('InProgressGameOverlay: Stale daily data detected, closing overlay', {
          savedDate,
          today,
          mode,
          timestamp: new Date().toISOString()
        });
        onClose();
        return;
      }
      
      
      // Load game state info
      const savedWord = directCookieAccess.getCookie(`${prefix}current_word`);
      const savedLevel = directCookieAccess.getCookie(`${prefix}current_level`);
      const savedAttempts = directCookieAccess.getCookie(`${prefix}current_guess_row`);
      
      // Get the guess history for the current level
      const guessHistoryStr = directCookieAccess.getCookie(`${prefix}guess_history`);
      let currentLevelGuesses: { letter: string; status: string }[][] = [];
      
      if (guessHistoryStr) {
        try {
          const guessHistory = JSON.parse(guessHistoryStr);
          const savedLevelNumber = savedLevel ? parseInt(savedLevel, 10) : 1;
          
          // Find the entries for current level
          const levelHistory = guessHistory.find(
            (h: any) => h.level === savedLevelNumber && h.word === savedWord
          );
          
          if (levelHistory && levelHistory.guesses) {
            currentLevelGuesses = levelHistory.guesses;
            console.log('Found guesses for current level:', {
              level: savedLevelNumber,
              word: savedWord,
              guessCount: currentLevelGuesses.length,
              guessDetails: currentLevelGuesses.map(g => g.map(l => l.letter).join('')),
              timestamp: new Date().toISOString()
            });
          }
        } catch (error) {
          console.error('Error parsing guess history:', error);
        }
      }
      
      // Fallback to saved_guesses if guess_history doesn't have current level
      if (currentLevelGuesses.length === 0) {
        const savedGuesses = directCookieAccess.getCookie(`${prefix}saved_guesses`);
        if (savedGuesses) {
          try {
            const parsedGuesses = JSON.parse(savedGuesses);
            if (Array.isArray(parsedGuesses) && parsedGuesses.length > 0) {
              currentLevelGuesses = parsedGuesses;
              console.log('Using saved_guesses fallback:', {
                guessCount: currentLevelGuesses.length,
                timestamp: new Date().toISOString()
              });
            }
          } catch (error) {
            console.error('Error parsing saved guesses:', error);
          }
        }
      }
      
      if (savedWord) setCurrentWord(savedWord);
      if (savedLevel) setCurrentLevel(parseInt(savedLevel, 10));
      if (savedAttempts) setAttempts(parseInt(savedAttempts, 10));
      setGuesses(currentLevelGuesses);
      setHasGuesses(currentLevelGuesses.length > 0);
      
      console.log('Loaded in-progress game details:', {
        mode,
        word: savedWord,
        level: savedLevel,
        attempts: savedAttempts,
        guessesFound: currentLevelGuesses.length > 0,
        guessCount: currentLevelGuesses.length,
        timestamp: new Date().toISOString()
      });
    } else {
      setVisible(false);
    }
  }, [isOpen, currentMode]);
  
  // Listen for mode switch events
  useEffect(() => {
    const handleModeSwitch = () => {
      console.log('Mode switch detected in InProgressGameOverlay', {
        timestamp: new Date().toISOString()
      });
      
      // Check if we should show the overlay for the new mode
      // Use directCookieAccess from the imported module, not from CookieManager
      const inProgressOverlayShown = directCookieAccess.getCookie(
        `${currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_'}in_progress_overlay_shown`
      );
      
      // Don't show overlay if it's already been shown for this mode
      if (inProgressOverlayShown === 'true') {
        onClose();
      }
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, [currentMode, onClose]);
  
  if (!isOpen) return null;

  const getStatusClass = (status: string): "correct" | "wrong-position" | "incorrect" | "empty" => {
    if (status === "misplaced" || status === "wrong-position") return "wrong-position";
    if (status === "correct") return "correct";
    if (status === "incorrect") return "incorrect";
    return "empty";
  };
  
  // CRITICAL FIX: Sets flags consistently in both cookie AND sessionStorage
  const handleContinuePlay = () => {
    const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_daily_';
    
    // Set the flag in cookie (via modeAwareStorage for consistency)
    modeAwareStorage.setCookie('in_progress_overlay_shown', 'true');
    
    // Also set in sessionStorage (already set when overlay opened, but ensure it's there)
    sessionStorage.setItem(`${prefix}in_progress_overlay_shown_this_session`, 'true');
    
    // CRITICAL FIX: Mark session as active to prevent unwanted redirects
    sessionStorage.setItem('babblegram_game_session_active', 'true');
    
    console.log('Setting in_progress_overlay_shown flags and marking session active', {
      mode: currentMode,
      prefix,
      inCookie: true,
      inSession: true,
      sessionActive: true,
      timestamp: new Date().toISOString()
    });
    
    onClose();
  };
  
  const handleGoToHome = () => {
    onClose();
    navigate('/');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      <div 
        className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 space-y-2 rounded-xl bg-gray-900 p-6 shadow-xl" 
        style={{ border: "5px solid #454545" }}
      >
        <h2 className="text-xl font-bold text-center text-[#de0046] mb-4">Continue Where You Left Off?</h2>
        
        <div className="space-y-4">
          <p className="text-gray-300 text-sm text-center">
            You have a game in progress at level {currentLevel}. 
            {attempts > 0 && ` You've made ${attempts} attempt${attempts > 1 ? 's' : ''} so far.`}
          </p>
          
          {currentWord && (
            <div className="flex flex-col items-center">
              <div className="bg-gray-800 p-3 rounded-md">
                <p className="text-xs text-gray-400 mb-2 text-center">Your progress:</p>
                
                {/* Display previous guesses if any */}
                {hasGuesses && guesses.length > 0 ? (
                  <div className="space-y-1 mb-2">
                    {guesses.map((guess, index) => (
                      <div key={index} className="flex justify-center gap-1">
                        {guess.map((letter, letterIndex) => (
                          <LetterBox
                            key={letterIndex}
                            letter={letter.letter}
                            status={getStatusClass(letter.status)}
                            isCompact={true}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center mb-2">
                    <p className="text-gray-300 text-sm">
                      {attempts === 0 
                        ? "Ready to start this level!" 
                        : "No valid guesses have been made yet."}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-center mt-2">
                  {currentWord.split('').map((letter, i) => (
                    <div 
                      key={i} 
                      className="w-7 h-7 flex items-center justify-center border-2 border-gray-700 mx-0.5 text-gray-400"
                    >
                      ?
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-gray-400 text-center mt-2">Word length: {currentWord.length} letters</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-center gap-3 mt-6">
          <Button onClick={handleContinuePlay} className="bg-[#de0046] hover:bg-[#de0046]/90 py-3 px-6 text-sm">
            Continue Playing <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          <Button onClick={handleGoToHome} variant="outline" className="text-gray-400 border-gray-700">
            Back to Home <Home className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        <div className="text-gray-500 text-xs text-center w-full mt-2">
          © {new Date().getFullYear()} BabbleGram. Created by Dara Walsh
        </div>
      </div>
    </div>
  );
};

export default InProgressGameOverlay;
